import { useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, ButtonProps } from '@givebutter/primitives/button';
import { DEFAULT_COLORS } from '@givebutter/primitives/color-picker';
import { useToast } from '@givebutter/primitives/toast';
import { isAcceptableThemeColor } from '@givebutter/util';
import { faPaintBrush } from '@fortawesome/pro-solid-svg-icons';
import { updateAccount } from 'api/account';
import * as Form from 'components/form';
import { usePanelContext } from 'components/profile-panel/PanelContext';
import * as PanelPopover from 'components/profile-panel/PanelPopover';
import { ProfileItemTriggerKeys } from 'components/profile-panel/PanelPopoverButtons';
import { usePanelPopoverContext } from 'components/profile-panel/PanelPopoverContext';
import { Settings } from 'constants/settings';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { Account, AccountSetting } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface ThemeColorSettingsFormValues {
    themeColor: string;
}

const defaultColor = DEFAULT_COLORS.filter(color => color.toUpperCase() !== '#FFFFFF')[0];

const ProfileColor = () => {
    const { setPlaceholderColor, setDisableExpandButtonOnHover } = usePanelContext();
    const { settings } = useCurrentAccount();

    const currentColor = useMemo(() => {
        return getColorValueFromSettings(settings);
    }, [settings]);

    const methods = Form.useGivebutterForm<ThemeColorSettingsFormValues>({
        defaultValues: {
            themeColor: currentColor ?? defaultColor,
        },
    });

    return (
        <PanelPopover.Root
            onOpenChange={open => {
                if (!open) {
                    methods.reset();
                    setPlaceholderColor(undefined);
                }
                setDisableExpandButtonOnHover(open);
            }}
        >
            <PanelPopover.Trigger asChild profileItem={ProfileItemTriggerKeys.Color}>
                <PanelPopover.IconExpandButton
                    icon={faPaintBrush}
                    onClick={() => {
                        setPlaceholderColor(currentColor ?? defaultColor);
                    }}
                >
                    Add theme
                </PanelPopover.IconExpandButton>
            </PanelPopover.Trigger>
            <PanelPopover.Content className="max-w-96">
                <Form.Provider {...methods}>
                    <PanelPopover.Header>
                        <PanelPopover.Title icon={faPaintBrush}>Theme color</PanelPopover.Title>
                        {currentColor && <RemoveButton />}
                    </PanelPopover.Header>
                    <FormContent />
                </Form.Provider>
            </PanelPopover.Content>
        </PanelPopover.Root>
    );
};

interface RemoveButtonProps extends Omit<ButtonProps, 'variant' | 'color' | 'size'> {}
const RemoveButton = ({ onClick, ...rest }: RemoveButtonProps) => {
    const { setPlaceholderColor, setDisableExpandButtonOnHover } = usePanelContext();
    const { accountId, settings } = useCurrentAccount();
    const { success, error } = useToast();
    const { close } = usePanelPopoverContext();

    const themeColorSetting = useMemo(() => {
        return getColorValueFromSettings(settings);
    }, [settings]);

    const { reset } = Form.useFormContext<ThemeColorSettingsFormValues>();

    const { mutate: clearColorSetting, isPending: isRemoving } = useMutation<
        Account,
        GivebutterAxiosError
    >({
        mutationFn: () =>
            updateAccount(accountId, {
                settings: [
                    {
                        type: 'string',
                        code: Settings.ThemeColor.Code,
                        name: themeColorSetting?.name ?? 'theme_color',
                        value: null,
                        active: false,
                    },
                ],
            }),
        onSuccess: () => {
            success('Cleared theme color');
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.current(accountId) });
            reset({
                themeColor: defaultColor,
            });
            setPlaceholderColor(undefined);
            setDisableExpandButtonOnHover(false);
            close();
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data?.message ?? 'Failed to clear theme color');
        },
    });

    return (
        <Button
            variant="transparent"
            color="neutral"
            size="sm"
            className="hover:bg-gray-100"
            onClick={e => {
                clearColorSetting();
                onClick?.(e);
            }}
            disabled={isRemoving}
            {...rest}
        >
            {isRemoving ? 'Removing...' : 'Remove'}
        </Button>
    );
};

const FormContent = () => {
    const { accountId, settings } = useCurrentAccount();
    const { close } = usePanelPopoverContext();
    const { setPlaceholderColor, setDisableExpandButtonOnHover } = usePanelContext();
    const { success, error } = useToast();

    const { control, handleSubmit, path, watch, reset } =
        Form.useFormContext<ThemeColorSettingsFormValues>();

    const themeColorSetting = useMemo(() => {
        return getColorValueFromSettings(settings);
    }, [settings]);

    const [themeColor] = watch(['themeColor']);

    const { mutate: submit, isPending: isLoading } = useMutation<
        Account,
        GivebutterAxiosError,
        ThemeColorSettingsFormValues
    >({
        mutationFn: ({ themeColor }) =>
            updateAccount(accountId, {
                settings: [
                    {
                        type: 'string',
                        code: Settings.ThemeColor.Code,
                        name: themeColorSetting?.name ?? 'theme_color',
                        value: themeColor,
                        active: !!themeColor,
                    },
                ],
            }),
        onSuccess: (_response, values) => {
            success('Updated theme color');
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.current(accountId) });
            setDisableExpandButtonOnHover(false);
            close();
            reset(values);
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data?.message ?? 'Unable to update theme color');
        },
    });

    return (
        <form onSubmit={handleSubmit(values => submit(values))}>
            <div className="flex flex-col gap-4 p-4">
                <Form.Container className="gap-3 px-2">
                    <p className="text-sm text-gray-600">
                        Appears across your campaigns, including your donate button, goal bar, and
                        background.
                    </p>
                    <Form.Label className="sr-only" name={path('themeColor')}>
                        Theme color
                    </Form.Label>
                    <Form.UseFormColorPicker
                        control={control}
                        name={path('themeColor')}
                        colors={DEFAULT_COLORS.filter(color => color.toUpperCase() !== '#FFFFFF')}
                        rules={{
                            validate: value =>
                                isAcceptableThemeColor(value)
                                    ? true
                                    : 'Choose a darker color for better readability.',
                        }}
                        onValueChange={color => {
                            setPlaceholderColor(color);
                        }}
                    />
                    <Form.Error name={path('themeColor')} />
                </Form.Container>
                <div className="flex justify-between">
                    <PanelPopover.CancelButton />
                    <Form.SubmitButton
                        size="sm"
                        isSubmitting={isLoading}
                        disabled={themeColorSetting?.value === themeColor}
                    >
                        Save
                    </Form.SubmitButton>
                </div>
            </div>
        </form>
    );
};

const getColorValueFromSettings = (settings: AccountSetting[]) => {
    return settings.find(item => item.code === Settings.ThemeColor.Code)?.value;
};

export { ProfileColor };
